import { useMemo, useRef, useState } from "react";
import { Oval } from "react-loader-spinner";
import Viewer from "react-viewer";
import { formatDate, getAvatarUrl } from "src/Common/common";
import WhiteUploadButton from "src/Components/Common/whiteuploadbutton";
import PDFPreviewModal from "src/Pages/WO/Components/pdfpreview";

const getNoteText = (note) => {
  return note?.message || note?.text || note?.note || note?.content || note?.body || "";
};

const getNoteAuthor = (note) => {
  if (note?.senderUser) return note.senderUser;
  if (note?.senderCustomer) return note.senderCustomer;
  return { name: "Unknown", avatar: null };
};

const getAttachmentUrl = (attachment) => {
  if (!attachment) return null;
  if (typeof attachment === "string") return attachment;
  return (
    attachment?.url ||
    attachment?.path ||
    attachment?.fileUrl ||
    attachment?.location ||
    attachment?.link ||
    null
  );
};

const getInitials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(" ");
  const initials = parts.slice(0, 2).map((part) => part[0]).join("");
  return initials.toUpperCase();
};

const ChatSection = ({ notes = [], onSend }) => {
  const [noteText, setNoteText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [isImageViewerShown, setIsImageViewerShown] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewImageActiveIndex, setPreviewImageActiveIndex] = useState(0);
  const [isPDFViewerShown, setIsPDFViewerShown] = useState(false);
  const [previewPDFs, setPreviewPDFs] = useState([]);
  const [previewPDFActiveIndex, setPreviewPDFActiveIndex] = useState(0);
  const [isSending, setIsSending] = useState(false);

  const isPdfFile = (value) => {
    if (!value) return false;
    const normalized = String(value).split("?")[0].toLowerCase();
    return normalized.endsWith(".pdf");
  };

  const handleAttachmentClick = (attachments, attachment) => {
    const clickedUrl = getAttachmentUrl(attachment);
    if (!clickedUrl) return;

    if (isPdfFile(clickedUrl)) {
      const pdfUrls = [];
      let activeIndex = 0;

      attachments.forEach((item) => {
        const url = getAttachmentUrl(item);
        if (url && isPdfFile(url)) {
          if (url === clickedUrl) {
            activeIndex = pdfUrls.length;
          }
          pdfUrls.push(url);
        }
      });

      setPreviewPDFs(pdfUrls);
      setPreviewPDFActiveIndex(activeIndex);
      setIsPDFViewerShown(true);
      return;
    }

    const imageFiles = [];
    let activeIndex = 0;

    attachments.forEach((item) => {
      const url = getAttachmentUrl(item);
      if (url && !isPdfFile(url)) {
        if (url === clickedUrl) {
          activeIndex = imageFiles.length;
        }
        imageFiles.push({ src: url, alt: "" });
      }
    });

    setPreviewImages(imageFiles);
    setPreviewImageActiveIndex(activeIndex);
    setIsImageViewerShown(true);
  };

  const sortedNotes = useMemo(() => {
    if (!Array.isArray(notes)) return [];
    return [...notes].sort((a, b) => {
      const aTime = Date.parse(a?.createdAt || a?.createdOn || a?.time || 0) || 0;
      const bTime = Date.parse(b?.createdAt || b?.createdOn || b?.time || 0) || 0;
      return aTime - bTime;
    });
  }, [notes]);

  const handleSend = async (event) => {
    event.preventDefault();
    const trimmed = noteText.trim();
    if (!trimmed && selectedFiles.length === 0) return;
    if (isSending) return;

    if (typeof onSend === "function") {
      try {
        setIsSending(true);
        await Promise.resolve(onSend(trimmed, selectedFiles));
        setNoteText("");
        setSelectedFiles([]);
      } finally {
        setIsSending(false);
      }
    }
  };

  const handleSelectFiles = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setSelectedFiles((prev) => [...prev, ...files]);
    event.target.value = "";
  };

  const handleRemoveFile = (indexToRemove) => {
    setSelectedFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const canSend = noteText.trim() || selectedFiles.length;

  return (
    <div className="border border-gray-400 ">
      <Viewer
        visible={isImageViewerShown}
        onClose={() => setIsImageViewerShown(false)}
        images={previewImages}
        activeIndex={previewImageActiveIndex}
      />
      <PDFPreviewModal
        PDFs={previewPDFs}
        activeIndex={previewPDFActiveIndex}
        shown={isPDFViewerShown}
        onCancel={() => setIsPDFViewerShown(false)}
        useDirectUrl
      />
      <div className="border-b border-gray-400 px-4 py-3">
        <span className="font-OpenSansRegular font-[700] text-[14px] text-titleColor">
          Chat
        </span>
      </div>

      <div className="px-4 py-4">
        <div className="flex flex-col gap-4">
          {sortedNotes.length ? (
            sortedNotes.map((note, index) => {
              const author = getNoteAuthor(note);
              const avatarSrc = author?.avatar ? getAvatarUrl(author.avatar) : null;
              const createdAt = note?.createdAt;
              const attachments = Array.isArray(note?.attachments) ? note.attachments : [];
              const dateLabel = createdAt
                ? formatDate(createdAt, "MM/DD/YYYY - h:mm A")
                : "-";

              return (
                <div
                  key={note?.id || note?._id || note?.noteId || index}
                  className="flex flex-col gap-2 border-b border-gray-400 pb-4 last:border-b-0 last:pb-0"
                >
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-OpenSansRegular text-[12px] text-titleColor">
                      {dateLabel}
                    </span>
                    <div className="flex items-center gap-2 min-w-0">
                      {avatarSrc ? (
                        <img
                          src={avatarSrc}
                          alt={author?.name || "User avatar"}
                          className="h-[26px] w-[26px] rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-[26px] w-[26px] rounded-full bg-wo_color2 flex items-center justify-center text-[10px] font-OpenSansRegular text-titleColor">
                          {getInitials(author?.name)}
                        </div>
                      )}
                      <span className="font-OpenSansRegular text-[12px] text-titleColor text-right">
                        {author?.name || "Unknown"}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="font-OpenSansRegular text-[12px] text-titleColor">
                      {getNoteText(note) || "-"}
                    </p>
                      {attachments.length ? (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {attachments.map((attachment, attachmentIndex) => {
                            const src = getAttachmentUrl(attachment);
                            const isPdf = isPdfFile(src);
                            return (
                              <button
                                type="button"
                                key={attachment?.id || src || attachmentIndex}
                                onClick={() => handleAttachmentClick(attachments, attachment)}
                                className="h-[54px] w-[54px] rounded-[6px] border border-gray-400 bg-[#f7f7fb] flex items-center justify-center overflow-hidden"
                              >
                                {src && !isPdf ? (
                                  <img
                                    src={src}
                                    alt="Attachment"
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <span className="font-OpenSansRegular text-[10px] text-titleColor">
                                    {isPdf ? "PDF" : "FILE"}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      ) : null}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center font-OpenSansRegular text-[12px] text-subTitleColor">
              No messages yet.
            </div>
          )}
        </div>

        <form onSubmit={handleSend} className="mt-4">
          <textarea
            className="w-full min-h-[110px] rounded-[6px] border border-gray-400 px-4 py-3 font-OpenSansRegular text-[12px] text-titleColor placeholder:text-subTitleColor focus:outline-none"
            placeholder="Write a message"
            value={noteText}
            onChange={(event) => setNoteText(event.target.value)}
          />
          <div className="mt-3 rounded-[6px] border border-gray-400 px-3 py-2">
            <div className="flex items-center justify-between gap-3">
              <span className="font-OpenSansRegular font-[700] text-[12px] text-titleColor">
                Screenshots
              </span>
              <WhiteUploadButton
                clicked={() => fileInputRef.current?.click()}
                isLoading={false}
              />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleSelectFiles}
              className="hidden"
            />
            {selectedFiles.length ? (
              <div className="mt-3 flex flex-col gap-2">
                {selectedFiles.map((file, index) => (
                  <div
                    key={`${file?.name || "screenshot"}-${index}`}
                    className="flex items-center justify-between gap-3 rounded-[6px] border border-gray-400 px-3 py-1 w-[300px]"
                  >
                    <span className="truncate font-OpenSansRegular text-[12px] text-titleColor">
                      {file?.name || "Screenshot"}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      className="font-OpenSansRegular text-[10px] font-[600] text-subTitleColor"
                    >
                      REMOVE
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-3 font-OpenSansRegular text-[12px] text-subTitleColor">
                No screenshots selected.
              </div>
            )}
          </div>
          <div className="mt-3 flex justify-end">
            <button
              type="submit"
              disabled={!canSend || isSending}
              className="h-[34px] min-w-[96px] px-4 rounded-[6px]  font-OpenSansRegular text-[12px] font-[600] text-titleColor disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center bg-blueColor text-white"
            >
              {isSending ? (
                <Oval
                  visible={true}
                  height="20"
                  width="20"
                  color="#1d1d1f"
                  strokeWidth={8}
                  secondaryColor="#c7c7c7"
                  ariaLabel="send-note-loading"
                />
              ) : (
                "SEND"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatSection;
